

import { useNavigate } from 'react-router-dom';
import StickButtonsBloc from '../../blocs/common/StickButtonsBloc';
import { APP_URLS, COUNTRY_FRANCE_ID, ERROR_MESSAGES } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';
import { useEffect, useRef, useState } from 'react';
import { useFetchNomenclature } from '../../hooks/useFetchNomenclature';
import MgpFormSelect from '../../elements/MgpFormSelect/MgpFormSelect';
import { isPropositionInLock } from '../../utils/dataUtils';
import MgpAutocomplete from '../../elements/MgpAutocomplete/MgpAutocomplete';
import MgpAddressFormAutoComplete from '../../elements/MgpAddressFormAutoComplete/MgpAddressFormAutoComplete';
import { IAdresseSearchFeature } from '../../context/model';
import { cleanSpecialCharAddress, handleKeyDown, isMobile } from '../../utils/utils';
import MGProtectModal from '../../modals/common/MGProtectModal';


type InfoPersoStep2FormData = {
  address: string;
  postalCode: string;
  city: string;
  countryId: string;
  protectedAddress: boolean;
  departmentCode: string;
  protectedConditions: boolean;
  lieudit: string;
  additionalAddress: string;
  buildingName: string;
}

export default function InfoPersoStep2FormComponent() {

  const navigate = useNavigate();

  const {
    state: { proposition, updatedProposition, showModal },
    dispatch,
  } = useAppState();

  
  const [openModal, setOpenModal] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const countryNomenclature = useFetchNomenclature("country", true);
  let departmentNomenclature = useFetchNomenclature("department", true);
  const onNextAction = (data: InfoPersoStep2FormData) => {

    let tempUpdatedPropo = updatedProposition;
    if(tempUpdatedPropo) {
      tempUpdatedPropo.address = cleanSpecialCharAddress(data.address);
      tempUpdatedPropo.postalCode = data.postalCode;
      tempUpdatedPropo.city = cleanSpecialCharAddress(data.city);
      tempUpdatedPropo.countryId = data.countryId;
      tempUpdatedPropo.departmentCode = data.departmentCode.split('-')[0].trim();
      tempUpdatedPropo.lieudit = cleanSpecialCharAddress(data.lieudit);
      tempUpdatedPropo.additionalAddress = cleanSpecialCharAddress(data.additionalAddress);
      tempUpdatedPropo.buildingName = cleanSpecialCharAddress(data.buildingName);
      tempUpdatedPropo.protectedAddress = data.protectedAddress;
      tempUpdatedPropo.protectedConditions = data.protectedConditions;

      
      dispatch({type: 'NEXT_INFO_STEP', path: APP_URLS.infoPersoStep3Page, updatedProposition: tempUpdatedPropo});
      navigate({
        pathname: APP_URLS.infoPersoStep3Page,
        search: `?id=${proposition!.propositionId}`,
      });
    }  
  }
  const onPreviousAction = () => {
    dispatch({type: 'BACK_INFO_STEP', path: APP_URLS.infoPersoStep1Page});
    navigate({
      pathname: APP_URLS.infoPersoStep1Page,
      search: `?id=${proposition!.propositionId}`,
    });
  }

  const validationSchema = Yup.object().shape({
    address: Yup.string().when('protectedAddress', {
      is: false,
      then: Yup.string()
              .required(ERROR_MESSAGES.requiredMsg)
              .min(5, ERROR_MESSAGES.invalidMsg)
              .max(38, ERROR_MESSAGES.invalidMsg)
              .nullable()
    }),
    postalCode: Yup.string().when('protectedAddress', {
      is: false,
      then: Yup.string()
                .when('countryId', {
                  is: (val: any) => val === COUNTRY_FRANCE_ID,
                  then: Yup.string()
                          .required(ERROR_MESSAGES.requiredMsg)
                          .matches(/^[0-9]+$/, ERROR_MESSAGES.invalidMsg )
                          .min(5, ERROR_MESSAGES.invalidMsg)
                          .max(5, ERROR_MESSAGES.invalidMsg)
                })
              .when('countryId', {
                is: (val: any) => val !== COUNTRY_FRANCE_ID,
                then: Yup.string()
                        .required(ERROR_MESSAGES.requiredMsg)
                        .matches(/^[a-zA-Z0-9-]+$/, ERROR_MESSAGES.invalidMsg )
                        .min(4, ERROR_MESSAGES.invalidMsg)
                        .max(8, ERROR_MESSAGES.invalidMsg)
              }),
    }),
    city: Yup.string().when('protectedAddress', {
      is: false,
      then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
    }),
    countryId: Yup.string().when('protectedAddress', {
      is: false,
      then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
    }),
    departmentCode: Yup.string().when('protectedAddress', {
       is: true,
       then: Yup.string()
       .test('departementCode', ERROR_MESSAGES.invalidMsg, function(value) {
        const code = value?.split('-')[0].trim() ?? '';
        const arr = departmentNomenclature.options.map(el => el.code);
        return arr.indexOf(code) !== -1;
       })
       .required(ERROR_MESSAGES.requiredMsg),
      }),
    protectedConditions: Yup.bool().when('protectedAddress', {
      is: true,
      then: Yup.bool().oneOf([true],ERROR_MESSAGES.requiredMsg)
    }),
  });

  const {
    register,
    handleSubmit,
    setValue, watch,
    setError,
    formState: { errors },
  } = useForm<InfoPersoStep2FormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      address: updatedProposition?.address ? updatedProposition?.address : '',
      postalCode: updatedProposition?.postalCode ? updatedProposition?.postalCode : '',
      city: updatedProposition?.city ? updatedProposition?.city : '',
      countryId: updatedProposition?.countryId ? updatedProposition?.countryId : '',
      protectedAddress: updatedProposition?.protectedAddress ? updatedProposition?.protectedAddress : false,
      departmentCode: '',
      protectedConditions: updatedProposition?.protectedConditions ? updatedProposition?.protectedConditions : false,
      lieudit: updatedProposition?.lieudit ? updatedProposition?.lieudit : '',
      additionalAddress: updatedProposition?.additionalAddress ? updatedProposition?.additionalAddress : '',
      buildingName: updatedProposition?.buildingName ? updatedProposition?.buildingName : ''
    },
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if(countryNomenclature && updatedProposition?.countryId) {
      setValue("countryId", updatedProposition.countryId);
    }
   
    if(!updatedProposition?.departmentCode) {
      setValue("departmentCode", "")
    } else if(updatedProposition?.postalCode?.startsWith("97")) {
      setValue("departmentCode", `${updatedProposition?.postalCode?.slice(0,3)} - ${departmentNomenclature?.options?.find(d => d.code === updatedProposition?.postalCode?.slice(0,3))?.label}`);
    } else {
      setValue("departmentCode", `${updatedProposition?.departmentCode} - ${departmentNomenclature?.options?.find(d => d.code === updatedProposition?.departmentCode)?.label}`);
    }
   
    dispatch({type: 'SHOW_MODAL', showModal: false});
  }, [countryNomenclature, departmentNomenclature]);

  const onCloseModal = () => {
    setValue('protectedAddress', false);
    setValue('protectedConditions', false);
    setOpenModal(false);
    dispatch({type: 'SHOW_MODAL', showModal: false});
  };

  const onConfirmAction = () => {
    setValue('protectedAddress', true);
    setValue('protectedConditions', true);
    setOpenModal(false);
    dispatch({type: 'SHOW_MODAL', showModal: false});
  }

  const onChangeCheckbox = (e: any) => {
    setOpenModal(e.target.checked);
    dispatch({type: 'SHOW_MODAL', showModal: true});
    if (!e.target.checked) {
      dispatch({type: 'SHOW_MODAL', showModal: false});
      setValue('protectedAddress', false);
      setValue('protectedConditions', false);
      setValue('address', updatedProposition!.address);
    }
  }

  function onAddressSelect(data:IAdresseSearchFeature) {
    setValue("address", data.properties.name);
    setValue("postalCode", data.properties.postcode);
    setValue("city", data.properties.city);
    setValue("countryId", COUNTRY_FRANCE_ID);
  }
  const selectedCountryId =  watch("countryId", COUNTRY_FRANCE_ID);
  const isFranceAddress = selectedCountryId === COUNTRY_FRANCE_ID;

  const handleFocus = () => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start'});
  }

  const mgprotectContent = () => {
    return (
      <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs' ref={ref}>
        <div className=''>
          <h2 className='card-title bleu'>MGPROTECT Localisation</h2>
          <div className='form-group mw-100 mt-sm'>
            <p className="text" style={{textAlign: 'justify'}}>
              Vous pouvez souscrire gratuitement au service MGPROTECT Localisation.
              <br/>
              Il s'agit d'un dispositif proposé par la MGP vous permettant de ne pas communiquer votre adresse postale lors de votre affiliation. 
              <br/>
              Ainsi, vos coordonnées physiques ne sont stockées nulle part à la MGP. 
              <br/>
              Gratuit et non obligatoire, MGPROTECT Localisation repose sur un engagement mutuel de communiquer ensemble uniquement par voie électronique, par téléphone ou depuis votre espace adhérent sur mgp.fr.
              <br/>
              Après votre inscription au service MGPROTECT Localisation, vous pouvez y renoncer à tout moment en nous adressant une demande depuis votre espace adhérent.
            </p>
          </div>  
        </div> 
      

        <div className="mt-lg form-row" >
          <div className="ta-center form-group mw-100">
            <iframe width={isMobile() ? "100%" : "80%"} height="230" src="https://www.youtube.com/embed/LUzYn3r5ZGw?si=grSSMfy0nlkHG9md" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
          </div>
        </div>
      </div>
    )
  }

    return (            
      <form style={{width: '100%'}}>
        <div className='form-row page-content'>
          {!isMobile() && proposition?.configuration.canSubscribeMgpProtect && mgprotectContent()}

          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
            <div className='pb-xl'>
            {proposition?.configuration.canSubscribeMgpProtect && <h2 className='card-title bleu'>Souhaitez-vous bénéficier de MGPROTECT Localisation ? {isMobile() && <span className='info-badge info-icon ml-xs' onClick={handleFocus} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>i</span>}</h2>}
        
            <div>
              <MGProtectModal 
                isOpen={openModal}
                onConfirmAction={onConfirmAction}
                onCloseModal={onCloseModal}
                register={register}
                errors={errors}
                refuseButtonName="Je préfère ne pas m'inscrire à MGPROTECT Localisation"
                acceptButtonName="Je confirme ma demande d'inscription à MGPROTECT Localisation"
                watch={watch}
                setError={setError}
              />
            </div>
            
            <div className="form-row mb-md">
              <div className="form-group form-check mw-100">
                {proposition?.configuration.canSubscribeMgpProtect && 
                  <MgpFormCheckbox 
                      id='protectedAddress' 
                      label="Je souhaite m'inscrire à MGPROTECT Localisation"
                      labelFontWeight='medium'
                      labelColor='fushia'
                      register={register} 
                      onChange={(e) => onChangeCheckbox(e)} 
                  />     
                }
              </div>   
            </div>

            {
              watch('protectedAddress') ?
              <div className='pb-xl'>
                <div className="form-row">   
                  <div className="form-group mw-100 autocomplete">
                    <MgpAutocomplete 
                      options={departmentNomenclature.options}
                      label="Votre département"
                      labelFontWeight='regular'
                      id='departmentCode'
                      register={register} 
                      errors={errors}
                      required={true}
                      onDataSelect={(data) => {
                        setValue('departmentCode', `${data.code} - ${data.label}`);
                      }}
                    />
                  </div> 
                </div>
                <div className="form-row">
                  <div className="form-group mw-100 autocomplete">  
                    <p className='text bg-menthe blanc p-xs grid-flex va-center jc-start mt-lg'>
                      Le numéro de votre département est requis pour des raisons réglementaires et statutaires. Cette information est notamment obligatoire pour déterminer la composition de l’assemblée générale et effectuer les convocations. Ce numéro de département, suivi de deux ou trois 9, figurera dans tous les courriers électroniques que vous recevrez de la MGP.
                    </p>  
                  </div>
                </div>
              </div>
              :
              <div> 
                <div className='form-row'>
                  <div className="form-group mw-100">
                    <MgpFormInput
                      label="Complément du nom"
                      labelFontWeight='regular'
                      type="text"
                      register={register}
                      id="additionalAddress"
                      maxLength={38}
                      errors={errors}
                    />
                  </div>   
                </div>

                <div className='form-row'>
                  <div className="form-group mw-100">
                    <MgpFormInput
                      label="Résidence et bâtiment"
                      labelFontWeight='regular'
                      type="text"
                      register={register}
                      id="buildingName"
                      maxLength={38}
                      errors={errors}
                    />
                  </div>   
                </div>

                <div className='form-row'>
                  <div className="form-group mw-100 autocomplete">         
                      <MgpAddressFormAutoComplete 
                        label="Numéro et libellé de la voie"  
                        type="text"
                        register={register}
                        id="address"
                        stopSearch={selectedCountryId && selectedCountryId !== COUNTRY_FRANCE_ID ? true : false}
                        maxLength={38}
                        labelFontWeight='regular'
                        errors={errors} onDataSelect={(data) => {onAddressSelect(data)}} 
                        required={true}
                      />
                  </div> 
                </div> 

                <div className='form-row'>
                  <div className="form-group mw-100">
                      <MgpFormInput 
                        label="Lieu-dit, Hameau, Commune, BP"
                        labelFontWeight='regular'
                        id="lieudit"
                        maxLength={38}
                        register={register}
                        errors={errors}
                      />
                  </div>   
                </div>
     
                <div className='form-row'>
                  <div className="form-group mw-100">
                      <MgpFormInput
                            label="Code postal"
                            id="postalCode"
                            labelFontWeight='regular'
                            register={register} 
                            errors={errors}
                            required={true}
                            value={watch('postalCode')}
                            maxLength={isFranceAddress ? 5 : 8}
                      />
                  </div>
                </div>
                  
                <div className='form-row'>
                  <div className="form-group mw-100">
                    <MgpFormInput
                          label="Ville"
                          id="city"
                          labelFontWeight='regular'
                          register={register} 
                          errors={errors}
                          required={true}
                          value={watch('city')}
                      />
                  </div>
                </div>
                  
                <div className='form-row'>
                  <div className="form-group mw-100">
                    <MgpFormSelect id="countryId" data={countryNomenclature.options} label="Pays" labelFontWeight='regular' register={register} errors={errors} required={true}/>
                  </div>
                </div>       
              </div>
            }
            </div>

            <div className="pt-lg">
              <p className='text fw-medium'><span className='fushia'>(*)</span> Champs obligatoires</p>
            </div>
          </div>

          { isMobile() && proposition?.configuration.canSubscribeMgpProtect && mgprotectContent() }
          
        </div>
          
          {
            !showModal && <StickButtonsBloc disabledPreviousButton={proposition && isPropositionInLock(proposition) ? true : false} showNextButton={true} showPreviousButton={true} previousAction={onPreviousAction} nextAction={handleSubmit(onNextAction)}/>
          }
        <div>
        </div>
      </form>
  )
}
